'use client'

import { ChevronDown } from 'lucide-react'
import { forwardRef, memo, useCallback, useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'

import { cn } from '../lib/cn'
import { throttle } from '../lib/throttle'

export interface InteractiveBlockProps {
  /** The lightweight component to show by default */
  children: ReactNode
  /** The full, interactive component to show on interaction. Receives a close function to switch back to the lightweight view. */
  Content: (close: () => void) => ReactNode
  contentClassName?: string
  className?: string
  /** Trailing chevron hinting the value is editable. Leave off when the child draws its own (e.g. `<Badge hasChevron />`). */
  hasChevron?: boolean
}

const SCROLL_THROTTLE_TIMEOUT = 100

export const InteractiveBlock = memo(
  forwardRef<HTMLDivElement, InteractiveBlockProps>(
    ({ children, Content, contentClassName, className, hasChevron = false }, forwardedRef) => {
      const containerRef = useRef<HTMLDivElement>(null)
      const [isEditing, setIsEditing] = useState(false)

      const setRefs = useCallback(
        (node: HTMLDivElement | null) => {
          containerRef.current = node

          if (typeof forwardedRef === 'function') {
            forwardedRef(node)
          } else if (forwardedRef) {
            forwardedRef.current = node
          }
        },
        [forwardedRef],
      )

      const onClose = useCallback(() => {
        setIsEditing(false)
      }, [])

      useEffect(() => {
        if (!isEditing) return

        const handleScrollThrottled = throttle(() => {
          if (containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect()
            const isOutsideViewport =
              rect.bottom < 0 || rect.top > window.innerHeight || rect.right < 0 || rect.left > window.innerWidth

            if (isOutsideViewport) {
              onClose()
            }
          }
        }, SCROLL_THROTTLE_TIMEOUT)

        window.addEventListener('scroll', handleScrollThrottled, true)

        return () => {
          handleScrollThrottled.cancel()
          window.removeEventListener('scroll', handleScrollThrottled, true)
        }
      }, [isEditing, onClose])

      const setEditing = useCallback(() => {
        setIsEditing(true)
      }, [])

      const onKeyDown = useCallback(
        (event: KeyboardEvent<HTMLDivElement>) => {
          if (event.key === 'Enter') {
            setEditing()
          } else if (event.key === ' ') {
            event.preventDefault()
            setEditing()
          }
        },
        [setEditing],
      )

      if (isEditing) {
        return (
          <div ref={setRefs} className={contentClassName}>
            {Content(onClose)}
          </div>
        )
      }

      return (
        <div
          ref={setRefs}
          role='button'
          tabIndex={0}
          onClick={setEditing}
          onKeyDown={onKeyDown}
          className={cn(
            'group cursor-pointer flex flex-1 h-full gap-1 border border-transparent hover:border-border items-center justify-center rounded-lg',
            className,
          )}
        >
          {children}
          {/* Hints that the value is editable; brightens on hover/focus. */}
          {hasChevron && (
            <ChevronDown
              aria-hidden
              className='size-3.5 shrink-0 text-muted-foreground opacity-60 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100'
            />
          )}
        </div>
      )
    },
  ),
)
InteractiveBlock.displayName = 'InteractiveBlock'
